import { useRef, useState } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { synthwave84 } from 'react-syntax-highlighter/dist/esm/styles/prism';
import {
    StanButton,
    StanFileDropZone,
    StanUnsplashImageCropSelector,
    type StanUnsplashImageCropSelectorRef,
} from '@/components';
import { useModal } from '@/hooks/useModal';
import { stanNotify } from '@/lib';

export default function InternalsFileUploadPage() {
    const [activePlayground, setActivePlayground] = useState('preview');
    const unsplashImageSelectorModal = useModal();
    const [croppedImageSrc, setCroppedImageSrc] = useState<string | null>(null);
    const [croppedFileName, setCroppedFileName] = useState<string | null>(null);
    const [croppedFileUrl, setCroppedFileUrl] = useState<string | null>(null);
    const unsplashImageCropSelectorRef = useRef<StanUnsplashImageCropSelectorRef>(null);

    const CodeBlock = ({ code }: { code: string }) => (
        <div className='border-muted overflow-hidden rounded-md border'>
            <div className='text-body-md [&_code]:w-full! [&_code]:!p-0 [&_code]:!text-base [&_code]:!leading-relaxed [&_pre]:!m-0 [&_pre]:!p-6'>
                <SyntaxHighlighter language='typescript' style={synthwave84} showLineNumbers={false}>
                    {code.trim()}
                </SyntaxHighlighter>
            </div>
        </div>
    );

    return (
        <div className='mx-auto max-w-6xl space-y-12 p-6 pb-40'>
            {/* Page Header */}
            <div className='space-y-2'>
                <h1 className='text-heading-h1 text-dark'>Unsplash Image Selector</h1>
                <p className='text-body-lg text-gray'>
                    Modal to allow user to select an image locally or from Unsplash. Returns the url blob or
                    downlaodable image url
                </p>
            </div>
            {/* Interactive Playground */}
            <section className='space-y-6'>
                <div>
                    <h2 className='text-heading-h2 text-dark'>Interactive Unsplash Image Selector With Crop</h2>
                    <p className='text-body-md text-gray mt-2'>
                        Select an image from unsplash and crop it to the desired size
                    </p>
                </div>

                <div className='border-muted rounded-lg border'>
                    <div className='border-muted flex gap-6 border-b px-6 pt-2'>
                        <button
                            onClick={() => setActivePlayground('preview')}
                            className={`text-label-md-medium border-b-2 px-1 pb-1 font-semibold transition-all ${
                                activePlayground === 'preview'
                                    ? 'border-brand-main text-brand-main tracking-wider'
                                    : 'text-gray hover:text-dark border-transparent'
                            }`}
                        >
                            Preview
                        </button>
                        <button
                            onClick={() => setActivePlayground('code')}
                            className={`text-label-md-medium border-b-2 px-1 pb-1 font-semibold transition-all ${
                                activePlayground === 'code'
                                    ? 'border-brand-main text-brand-main tracking-wider'
                                    : 'text-gray hover:text-dark border-transparent'
                            }`}
                        >
                            Code
                        </button>
                    </div>

                    <div className='p-6'>
                        {activePlayground === 'preview' ? (
                            <div className='grid grid-cols-1 gap-8 lg:grid-cols-2'>
                                {/* Form Section */}
                                <div className='space-y-6'>
                                    {!croppedImageSrc && (
                                        <div className='space-y-4'>
                                            <p className='text-body-md text-gray'>No image selected</p>
                                        </div>
                                    )}
                                    {croppedImageSrc && (
                                        <>
                                            <img
                                                src={croppedImageSrc}
                                                alt='Product Image'
                                                className='w-1/2'
                                                onLoad={() => {
                                                    // Clear any memory used by the image blob url
                                                    URL.revokeObjectURL(croppedImageSrc);
                                                }}
                                            />
                                            <a href={croppedFileUrl ?? ''} target='_blank' rel='noopener noreferrer'>
                                                {croppedFileName}
                                            </a>
                                        </>
                                    )}
                                    <StanButton
                                        name='upload-image'
                                        onClick={() => unsplashImageCropSelectorRef.current?.open()}
                                    >
                                        Select Image
                                    </StanButton>
                                    <StanUnsplashImageCropSelector
                                        ref={unsplashImageCropSelectorRef}
                                        onClose={() => unsplashImageSelectorModal.close()}
                                        onCropped={(
                                            croppedDataUrl: string,
                                            croppedFileName: string,
                                            croppedFileUrl: string
                                        ) => {
                                            // For usage right away
                                            setCroppedImageSrc(croppedDataUrl);
                                            // To be uploaded to the server
                                            setCroppedFileName(croppedFileName);
                                            setCroppedFileUrl(croppedFileUrl);
                                        }}
                                    />
                                </div>
                            </div>
                        ) : (
                            <CodeBlock
                                code={`import { useModal } from '@/hooks/useModal';
import { useRef, useState } from 'react';
import { StanButton, StanUnsplashImageCropSelector, type StanUnsplashImageCropSelectorRef } from '@/components';

export default function UnsplashImageSelectorPlayground() {
    const unsplashImageSelectorModal = useModal();
    const [croppedImageSrc, setCroppedImageSrc] = useState('');
    const [croppedFileName, setCroppedFileName] = useState<string | null>(null);
    const [croppedFileUrl, setCroppedFileUrl] = useState<string | null>(null);
    const unsplashImageCropSelectorRef = useRef<StanUnsplashImageCropSelectorRef>(null);

    return (
        <div className='grid grid-cols-1 gap-8 lg:grid-cols-2'>
            {/* Form Section */}
            <div className='space-y-6'>
                {!croppedImageSrc && (
                    <div className='space-y-4'>
                        <p className='text-body-md text-gray'>No image selected</p>
                    </div>
                )}
                {croppedImageSrc && (
                    <>
                        <img
                            src={croppedImageSrc}
                            alt='Product Image'
                            className='w-1/2'
                            onLoad={() => {
                                // Clear any memory used by the image blob url
                                URL.revokeObjectURL(croppedImageSrc);
                            }}
                        />
                        <a href={croppedFileUrl ?? ''} target='_blank' rel='noopener noreferrer'>
                            {croppedFileName}
                        </a>
                    </>
                )}
                <StanButton name='upload-image' onClick={() => unsplashImageCropSelectorRef.current?.open()}>
                    Select Image
                </StanButton>
                <StanUnsplashImageCropSelector
                    ref={unsplashImageCropSelectorRef}
                    onClose={() => unsplashImageSelectorModal.close()}
                    onCropped={(croppedDataUrl: string, croppedFileName: string, croppedFileUrl: string) => {
                        // For usage right away
                        setCroppedImageSrc(croppedDataUrl);
                        // To be uploaded to the server
                        setCroppedFileName(croppedFileName);
                        setCroppedFileUrl(croppedFileUrl);
                    }}
                />
            </div>
        </div>
    );
}
`}
                            />
                        )}
                    </div>
                </div>
            </section>

            {/* Interactive Playground */}
            <section className='space-y-6'>
                <div>
                    <h2 className='text-heading-h2 text-dark'>Interactive File Drop Zone</h2>
                    <p className='text-body-md text-gray mt-2'>Try uploading a file</p>
                </div>

                <div className='border-muted rounded-lg border'>
                    <div className='border-muted flex gap-6 border-b px-6 pt-2'>
                        <button
                            onClick={() => setActivePlayground('preview')}
                            className={`text-label-md-medium border-b-2 px-1 pb-1 font-semibold transition-all ${
                                activePlayground === 'preview'
                                    ? 'border-brand-main text-brand-main tracking-wider'
                                    : 'text-gray hover:text-dark border-transparent'
                            }`}
                        >
                            Preview
                        </button>
                        <button
                            onClick={() => setActivePlayground('code')}
                            className={`text-label-md-medium border-b-2 px-1 pb-1 font-semibold transition-all ${
                                activePlayground === 'code'
                                    ? 'border-brand-main text-brand-main tracking-wider'
                                    : 'text-gray hover:text-dark border-transparent'
                            }`}
                        >
                            Code
                        </button>
                    </div>

                    <div className='p-6'>
                        {activePlayground === 'preview' ? (
                            <div className='grid grid-cols-1 gap-8 lg:grid-cols-2'>
                                {/* Form Section */}
                                <div className='space-y-6'>
                                    <StanFileDropZone
                                        className='w-full'
                                        onFilesChanged={files => {
                                            // eslint-disable-next-line no-console
                                            console.log('files', files);
                                        }}
                                        onError={error => {
                                            stanNotify({ type: 'error', title: 'Uh oh!', text: error.message });
                                        }}
                                        objectType='only_max'
                                        placeholder='Thumbnail'
                                    />
                                </div>
                            </div>
                        ) : (
                            <CodeBlock
                                code={`import {StanFileDropZone } from '@/components';

export default function UnsplashImageSelectorPlayground() {
    return (
         <StanFileDropZone
            className='w-1/2'
            onFilesChanged={files => {
                console.log('files', files);
            }}
            onError={error => {
                stanNotify({ type: 'error', title: 'Uh oh!', text: error.message });
            }}
            objectType='only_max'
            placeholder='Thumbnail'
        />
    );
}
`}
                            />
                        )}
                    </div>
                </div>
            </section>

            {/* Interactive Playground */}
            <section className='space-y-6'>
                <div>
                    <h2 className='text-heading-h2 text-dark'>Interactive Multi File Drop Zone</h2>
                    <p className='text-body-md text-gray mt-2'>Try uploading multiple files</p>
                </div>

                <div className='border-muted rounded-lg border'>
                    <div className='border-muted flex gap-6 border-b px-6 pt-2'>
                        <button
                            onClick={() => setActivePlayground('preview')}
                            className={`text-label-md-medium border-b-2 px-1 pb-1 font-semibold transition-all ${
                                activePlayground === 'preview'
                                    ? 'border-brand-main text-brand-main tracking-wider'
                                    : 'text-gray hover:text-dark border-transparent'
                            }`}
                        >
                            Preview
                        </button>
                        <button
                            onClick={() => setActivePlayground('code')}
                            className={`text-label-md-medium border-b-2 px-1 pb-1 font-semibold transition-all ${
                                activePlayground === 'code'
                                    ? 'border-brand-main text-brand-main tracking-wider'
                                    : 'text-gray hover:text-dark border-transparent'
                            }`}
                        >
                            Code
                        </button>
                    </div>

                    <div className='p-6'>
                        {activePlayground === 'preview' ? (
                            <div className='grid grid-cols-1 gap-8 lg:grid-cols-2'>
                                {/* Form Section */}
                                <div className='space-y-6'>
                                    <StanFileDropZone
                                        className='w-full'
                                        onFilesChanged={files => {
                                            // eslint-disable-next-line no-console
                                            console.log('files', files);
                                        }}
                                        onError={error => {
                                            stanNotify({ type: 'error', title: 'Uh oh!', text: error.message });
                                        }}
                                        objectType='only_max'
                                        placeholder='Thumbnail'
                                        multiple
                                    />
                                </div>
                            </div>
                        ) : (
                            <CodeBlock
                                code={`import {StanFileDropZone } from '@/components';

export default function UnsplashImageSelectorPlayground() {
    return (
         <StanFileDropZone
            className='w-1/2'
            onFilesChanged={files => {
                console.log('files', files);
            }}
            onError={error => {
                stanNotify({ type: 'error', title: 'Uh oh!', text: error.message });
            }}
            objectType='only_max'
            placeholder='Thumbnail'
            multiple
        />
    );
}
`}
                            />
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}
